import { FC, useCallback, useState } from 'react';
import { Box, IconButton, Modal, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { useStyles, clsx } from '../../../utils/hooks/useStyles';
import Logo from '../logo/logo.component';
import SearchBar from '../header-search-bar/header-search-bar.component';
import Showable from '../showable';
import { eventsService } from '../../../services';
import useLink from '../../../utils/hooks/useLink';
import { burgerMenuStyles } from './burger-menu.styles';
import ExternalLink from '../../links/External-link';
import { TYPEFORM_URL, SHOP_SWEATPALS_URL, BOOK_DEMO_URL } from '../../../constants';
import { EventType } from '../../../services/types/event-types';
import { FadeSpinner } from '../../ui-v3/fade-spinner';

export interface BurgerMenuProps {
	isOpen: boolean;
	onClose(): void;
}

const BurgerMenu: FC<BurgerMenuProps> = ({ isOpen, onClose }) => {
	const styles = useStyles(burgerMenuStyles);
	const router = useRouter();

	const { makeEventLink } = useLink();

	const [isOpeningEventPage, setIsOpeningEventPage] = useState(false);
	const [showSearchResults, setShowSearchResults] = useState<boolean>(false);

	const { mutate: searchEvents, data: events } = useMutation(
		['burger-menu-events'],
		async ({ search }: { search: string }) =>
			eventsService.getPublicEventsList({
				limit: 5,
				search,
			})
	);

	const onClearSearch = () => {
		setShowSearchResults(false);
	};

	const onSearchSubmit = (search: string) => {
		if (search) {
			searchEvents({ search });
			setShowSearchResults(true);
		} else {
			onClearSearch();
		}
	};

	const openEventPage = useCallback(async (event: EventType): Promise<void> => {
		setIsOpeningEventPage(true);

		try {
			const eventId = event.baseId ?? event.id;
			const eventsCount = await eventsService.getEventsCountPublic({ eventIds: [eventId] });

			const eventLink = makeEventLink({
				eventAlias: event.alias,
				shortLocalInstance: eventsCount > 1 ? event.shortLocalInstance : undefined,
			});

			await router.push(eventLink);
		} finally {
			setIsOpeningEventPage(false);
		}
	}, [makeEventLink, router]);

	return (
		<Modal open={isOpen} onClose={onClose}>
			<Box sx={styles.root}>
				<Showable show={isOpeningEventPage}>
					<Box sx={styles.loader}>
						<FadeSpinner size={60} color="white" />
					</Box>
				</Showable>
				<Box sx={styles.header}>
					<Logo type="dark-text" />
					<Box>
						<IconButton onClick={onClose} sx={styles.closeButton}>
							<CloseRoundedIcon />
						</IconButton>
					</Box>
				</Box>
				<Box sx={styles.content}>
					<SearchBar fullWidth onSubmit={onSearchSubmit} onClear={onClearSearch} />

					<Showable show={!showSearchResults}>
						<Box sx={clsx(styles.flexColumn, styles.pt('40px'), styles.gap('40px'))}>
							<Link href="/events" passHref legacyBehavior>
								<Typography sx={styles.navLink}>discover</Typography>
							</Link>
							<Link href="/about" passHref legacyBehavior>
								<Typography sx={styles.navLink}>about</Typography>
							</Link>
							<ExternalLink href={TYPEFORM_URL}>
								<Typography sx={styles.navLink}>grow your community</Typography>
							</ExternalLink>
							<ExternalLink href={SHOP_SWEATPALS_URL}>
								<Typography sx={styles.navLink}>shop</Typography>
							</ExternalLink>
							<ExternalLink href={BOOK_DEMO_URL}>
								<Typography sx={styles.navLink}>book a demo</Typography>
							</ExternalLink>
						</Box>
					</Showable>

					<Showable show={showSearchResults}>
						<Box
							sx={clsx(styles.flexColumn, styles.pt('40px'), styles.gap('40px'), {
								overflow: 'hidden',
							})}
						>
							<Showable show={events?.data?.length}>
								{events?.data?.map(event => (
									<Box
										key={event.id}
										onClick={() => { openEventPage(event); }}
										sx={clsx(styles.flexRow, styles.gap('10px'), styles.itemsCenter, styles.cursorPointer)}
									>
										<CalendarTodayIcon />
										<Typography
											sx={{
												fontFamily: 'Integral CF',
												lineHeight: '1.2em',
												fontWeight: '500',
											}}
										>
											{event.name}
										</Typography>
									</Box>
								))}
							</Showable>

							<Showable show={!events?.data?.length}>
								<Box sx={clsx(styles.flexRow, styles.gap('10px'), styles.itemsCenter)}>
									<SearchOffIcon />
									<Typography
										sx={{
											fontFamily: 'Integral CF',
											lineHeight: '1.2em',
											fontWeight: '500',
										}}
									>
										No results found
									</Typography>
								</Box>
							</Showable>
						</Box>
					</Showable>
				</Box>
			</Box>
		</Modal>
	);
};

export default BurgerMenu;
